import { Alert, Button, Card, Form, Input, Space, Typography } from "antd";
import type { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import {
    DateRangePicker as ZhDateRangePicker,
    Grid as ZhGrid,
    Pagination as ZhPagination,
    Table as ZhTable,
    type TableColumnProps
} from "zhihao-ui-react";

import { DEFAULT_VALUE, LOG_DATE_FORMAT } from "@/config";
import request from "@/api";
import { dateFormat } from "@zhihaoscm/utils";

export interface ConversationLogRow {
    customerConversationLog?: {
        id?: number | string;
        sessionId?: string;
        sessionTitle?: string;
        createdTime?: string;
    };
    customer?: {
        institutionName?: string;
        realName?: string;
        mobile?: string;
        code?: string;
    };
}

export interface ConversationLogItem {
    createdTime?: string;
    question?: string;
    answer?: string;
}

export interface ConversationLogQuery {
    page: number;
    size: number;
    customerKeyword?: string;
    sessionKeyword?: string;
    startTime?: string;
    endTime?: string;
    sortKey?: string;
    sortOrder?: string;
}

export interface ConversationLogPageResult {
    list?: ConversationLogRow[];
    total?: number;
}

export interface AiHistoricalDialoguePageProps {
    loadLogs?: (query: ConversationLogQuery) => Promise<ConversationLogPageResult>;
}

export interface AiHistoricalDialogueDetailPageProps {
    loadDetail?: (id: string) => Promise<ConversationLogRow>;
    loadConversations?: (params: { logId: string }) => Promise<ConversationLogItem[]>;
}

const defaultQuery: ConversationLogQuery = { page: 1, size: 20 };

/** 复用 Vue AI历史对话分页接口和查询参数。 */
export async function loadConversationLogs(query: ConversationLogQuery): Promise<ConversationLogPageResult> {
    const response = await request.service.get<ConversationLogPageResult>("/customer-conversation-log/paging", {
        params: query
    });
    return response.data;
}

/** 复用 Vue AI历史对话详情接口。 */
export async function loadConversationLogDetail(id: string): Promise<ConversationLogRow> {
    const response = await request.service.get<ConversationLogRow>(`/customer-conversation-log/vo/${id}`);
    return response.data;
}

/** 复用 Vue AI历史对话问答记录接口。 */
export async function loadConversationLogItems(params: { logId: string }): Promise<ConversationLogItem[]> {
    const response = await request.service.get<ConversationLogItem[]>("/conversation-log/findConversationByLogId", {
        params
    });
    return response.data;
}

/** 将表单日期范围转换为 Vue 的 startTime/endTime 查询字段。 */
export function buildConversationLogQuery(values: {
    customerKeyword?: string;
    sessionKeyword?: string;
    time?: [Dayjs | string, Dayjs | string];
}): ConversationLogQuery {
    const query: ConversationLogQuery = { ...defaultQuery };
    if (values.customerKeyword) query.customerKeyword = values.customerKeyword;
    if (values.sessionKeyword) query.sessionKeyword = values.sessionKeyword;
    if (values.time?.[0] && values.time[1]) {
        query.startTime = typeof values.time[0] === "string" ? values.time[0] : values.time[0].format("YYYY-MM-DD");
        query.endTime = typeof values.time[1] === "string" ? values.time[1] : values.time[1].format("YYYY-MM-DD");
    }
    return query;
}

function formatCustomer(row: ConversationLogRow): string {
    const customer = row.customer;
    return (
        [customer?.institutionName || customer?.realName, customer?.mobile, customer?.code]
            .filter(Boolean)
            .join(" | ") || DEFAULT_VALUE
    );
}

function formatConversationTime(value?: string): string {
    return value ? dateFormat(value, LOG_DATE_FORMAT) : DEFAULT_VALUE;
}

/** AI历史对话列表，保留 Vue 的筛选字段、分页参数、列顺序和详情跳转。 */
export function AiHistoricalDialoguePage({ loadLogs = loadConversationLogs }: AiHistoricalDialoguePageProps) {
    const navigate = useNavigate();
    const [filterForm] = Form.useForm();
    const [rows, setRows] = useState<ConversationLogRow[]>([]);
    const [total, setTotal] = useState(0);
    const [query, setQuery] = useState<ConversationLogQuery>(defaultQuery);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    useEffect(() => {
        let disposed = false;
        void loadLogs(query)
            .then(result => {
                if (disposed) return;
                setRows(result.list || []);
                setTotal(result.total || 0);
                setErrorMessage(null);
            })
            .catch(() => {
                if (disposed) return;
                setRows([]);
                setTotal(0);
                setErrorMessage("AI历史对话加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadLogs, query]);

    /** 将 zhihao-ui-react 的创建时间排序转换为 Vue 的 created_time 参数。 */
    const handleSortChange = ({ order }: { prop?: string; order: "ascending" | "descending" | null }) => {
        setQuery(current => ({
            ...current,
            page: 1,
            sortKey: order ? "created_time" : undefined,
            sortOrder: order === "ascending" ? "asc" : order === "descending" ? "desc" : undefined
        }));
    };

    const submitFilters = (values: {
        customerKeyword?: string;
        sessionKeyword?: string;
        time?: [Dayjs | string, Dayjs | string];
    }) => {
        setQuery(buildConversationLogQuery(values));
    };

    // 用 zhihao-ui-react 列契约承接旧 Vue 对话列表字段和详情跳转入口。
    const columns: TableColumnProps<ConversationLogRow>[] = [
        {
            key: "sessionId",
            label: "会话ID",
            prop: "customerConversationLog",
            render: ({ row }) => {
                const id = row.customerConversationLog?.id;
                return (
                    <Button
                        aria-label={`会话ID ${id ?? DEFAULT_VALUE}`}
                        type="link"
                        onClick={() => void navigate(`/intelligentAssistant/aiHistoricalDialogue/${id ?? ""}`)}
                    >
                        {id ?? DEFAULT_VALUE}
                    </Button>
                );
            }
        },
        { key: "customer", label: "账号", prop: "customer", render: ({ row }) => formatCustomer(row) },
        {
            key: "sessionTitle",
            label: "对话名称",
            prop: "customerConversationLog",
            render: ({ row }) => row.customerConversationLog?.sessionTitle || DEFAULT_VALUE
        },
        {
            key: "createdTime",
            label: "创建时间",
            prop: "customerConversationLog",
            sortable: true,
            render: ({ row }) => formatConversationTime(row.customerConversationLog?.createdTime)
        }
    ];

    return (
        <section className="flex h-full min-h-full flex-col" aria-label="ai-historical-dialogue-page">
            <Card className="flex h-full flex-1 flex-col [&_.ant-card-body]:flex [&_.ant-card-body]:min-h-0 [&_.ant-card-body]:flex-1 [&_.ant-card-body]:flex-col">
                <ZhTable<ConversationLogRow>
                    data={rows}
                    columns={columns}
                    emptyText="暂无数据"
                    onSortChange={handleSortChange}
                    pagination={false}
                    rowKey={row => String(row.customerConversationLog?.id || row.customerConversationLog?.sessionId)}
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            <Typography.Title className="mb-4 mt-0" level={4}>
                                AI历史对话
                            </Typography.Title>

                            {errorMessage ? (
                                <Alert className="mb-4" message={errorMessage} role="alert" type="error" />
                            ) : null}

                            <Form
                                className="mb-4"
                                form={filterForm}
                                layout="inline"
                                onFinish={values => submitFilters(values)}
                            >
                                <Form.Item label="账号" name="customerKeyword">
                                    <Input aria-label="账号关键词" placeholder="请输入账号信息搜索" />
                                </Form.Item>
                                <Form.Item label="对话名称" name="sessionKeyword">
                                    <Input aria-label="对话名称关键词" placeholder="请输入对话名称关键词搜索" />
                                </Form.Item>
                                <Form.Item label="创建时间" name="time">
                                    <ZhDateRangePicker
                                        endPlaceholder="结束日期"
                                        format="YYYY-MM-DD"
                                        startPlaceholder="开始日期"
                                        type="daterange"
                                        valueFormat="YYYY-MM-DD"
                                    />
                                </Form.Item>
                                <Space>
                                    <Button aria-label="查询" htmlType="submit" type="primary">
                                        查询
                                    </Button>
                                    <Button
                                        aria-label="重置"
                                        onClick={() => {
                                            filterForm.resetFields();
                                            setQuery(defaultQuery);
                                        }}
                                    >
                                        重置
                                    </Button>
                                </Space>
                            </Form>
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={query.page}
                                pageSize={query.size}
                                total={total}
                                hideOnSinglePage
                                onChange={(page, size) => setQuery(current => ({ ...current, page, size }))}
                            />
                        </div>
                    }
                />
            </Card>
        </section>
    );
}

function replaceEmptyLines(value?: string): string {
    return (value || DEFAULT_VALUE).replace(/(\n){3,}/g, "\n\n");
}

/** AI历史对话详情，保留 Vue 的账号、会话名称和问答记录展示。 */
export function AiHistoricalDialogueDetailPage({
    loadDetail = loadConversationLogDetail,
    loadConversations = loadConversationLogItems
}: AiHistoricalDialogueDetailPageProps) {
    const navigate = useNavigate();
    const { id = "" } = useParams<{ id: string }>();
    const [detail, setDetail] = useState<ConversationLogRow>({});
    const [items, setItems] = useState<ConversationLogItem[]>([]);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    useEffect(() => {
        let disposed = false;
        void loadDetail(id)
            .then(result => {
                if (!disposed) setDetail(result);
            })
            .catch(() => {
                if (!disposed) setErrorMessage("AI历史对话详情加载失败");
            });
        void loadConversations({ logId: id })
            .then(result => {
                if (!disposed) setItems(result || []);
            })
            .catch(() => {
                if (!disposed) setErrorMessage("AI历史对话记录加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [id, loadConversations, loadDetail]);

    const customerName = detail.customer?.institutionName || detail.customer?.realName || DEFAULT_VALUE;

    return (
        <section className="flex h-full min-h-full flex-col" aria-label="ai-historical-dialogue-detail-page">
            <Card>
                <Typography.Title level={4}>AI历史对话详情</Typography.Title>
                {errorMessage ? <Alert className="mb-4" message={errorMessage} role="alert" type="error" /> : null}
                <Typography.Title level={5}>基本信息</Typography.Title>
                <ZhGrid
                    className="mb-6"
                    gap={[20, 20]}
                    cols={4}
                    style={{ gridTemplateColumns: "repeat(4, minmax(280px, 1fr))" }}
                >
                    <div>
                        <Typography.Text type="secondary">账号：</Typography.Text>
                        <span>{formatCustomer(detail)}</span>
                    </div>
                    <div>
                        <Typography.Text type="secondary">对话名称：</Typography.Text>
                        <span>{detail.customerConversationLog?.sessionTitle || DEFAULT_VALUE}</span>
                    </div>
                    <div>
                        <Typography.Text type="secondary">创建时间：</Typography.Text>
                        <span>{formatConversationTime(detail.customerConversationLog?.createdTime)}</span>
                    </div>
                </ZhGrid>
                <Typography.Title level={5}>对话记录</Typography.Title>
                <div className="grid grid-cols-[auto_auto_1fr] gap-x-4 gap-y-3">
                    {items.flatMap((item, index) => [
                        <span key={`${index}-question-name`}>{customerName}</span>,
                        <span key={`${index}-question-time`} className="text-slate-400">
                            {formatConversationTime(item.createdTime)}
                        </span>,
                        <span key={`${index}-question-content`} className="whitespace-pre-line">
                            {replaceEmptyLines(item.question)}
                        </span>,
                        <span key={`${index}-answer-name`}>链小云</span>,
                        <span key={`${index}-answer-time`} className="text-slate-400">
                            {formatConversationTime(item.createdTime)}
                        </span>,
                        <span key={`${index}-answer-content`} className="whitespace-pre-line">
                            {replaceEmptyLines(item.answer)}
                        </span>
                    ])}
                </div>
                <Button className="mt-6" onClick={() => void navigate("/intelligentAssistant/aiHistoricalDialogue")}>
                    返回
                </Button>
            </Card>
        </section>
    );
}

export default AiHistoricalDialoguePage;
